import { useQuery, useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import React, { useContext, useEffect } from "react";
import { Dropdown, Spinner } from "react-bootstrap";

import { useRenderedQueries } from "@/hooks/useRenderedQueries";
import { syncAllEntities } from "@/lib/gas-api";
import { subscribeToSyncMetadataChanges, syncMetadataQueryOptions } from "@/queries/sync-metadata";
import { SessionContext } from "@/stores/SessionContext";
import { runBomSync } from "@/utils/sync/bom-sync";
import { ManualSyncButton } from "./ManualSyncButton";
import { SyncAlert } from "./SyncAlert";
import { SyncTime } from "./SyncTime";
import { runVendorItemCatalogSync } from "@/utils/sync/vendor-item-catalog-sync";

export const SyncStatusNavDropDown: React.FC<{ className?: string }> = ({ className }) => {
  const { session } = useContext(SessionContext);
  const [syncBOMRunning, setSyncBOMRunning] = React.useState(false);
  const [syncVendorItemCatalogRunning, setSyncVendorItemCatalogRunning] = React.useState(false);
  const [syncOthersRunning, setSyncOthersRunning] = React.useState(false);

  // Get all the queries that are currently being used for rendering, and determine the date of the oldest data
  const renderedQueries = useRenderedQueries();
  const dataUpdatedAts = renderedQueries.map((q) => q.dataUpdatedAt).filter((v) => typeof v === "number" && v > 0);
  const pageOldestDataLoadedAt = dataUpdatedAts.length ? new Date(Math.min(...dataUpdatedAts)) : new Date();

  // Fetch sync metadata and subscribe to changes (if user is logged in)
  const {
    isPending,
    isError,
    data: allSyncMetadata,
    error,
  } = useQuery({ ...syncMetadataQueryOptions, enabled: !!session }); // eslint-disable-line no-restricted-syntax -- This query is not relevant to page data staleness
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!session) return;
    const unsubscribe = subscribeToSyncMetadataChanges(queryClient);
    return () => void unsubscribe();
  }, [queryClient, session]);

  if (isError) return <LoadingNavItem>Error loading sync data: {error.message}</LoadingNavItem>;
  if (isPending) return <LoadingNavItem>Loading sync data...</LoadingNavItem>;

  // Extract the BOM and Vendor-Item Catalog sync metadata from the list
  const otherSyncMetadata = [...allSyncMetadata];
  const bomSyncMetadataIndex = otherSyncMetadata.findIndex((sync) => sync.entityType === "bom");
  const bomSyncMetadata = bomSyncMetadataIndex === -1 ? null : otherSyncMetadata.splice(bomSyncMetadataIndex, 1)[0];
  const vendorItemCatalogSyncMetadataIndex = otherSyncMetadata.findIndex(
    (sync) => sync.entityType === "vendoritemcatalog",
  );
  const vendorItemCatalogSyncMetadata =
    vendorItemCatalogSyncMetadataIndex === -1 ? null : (
      otherSyncMetadata.splice(vendorItemCatalogSyncMetadataIndex, 1)[0]
    );

  const anySyncLastUpdated = allSyncMetadata[0]?.lastUpdated;
  const anySyncLastUpdatedMin =
    anySyncLastUpdated && anySyncLastUpdated < pageOldestDataLoadedAt ? anySyncLastUpdated : pageOldestDataLoadedAt;
  const anySyncRunning = syncBOMRunning || syncVendorItemCatalogRunning || syncOthersRunning;
  const title = (
    <>
      {anySyncRunning ?
        <Spinner size="sm" />
      : <SyncAlert lastUpdated={anySyncLastUpdatedMin} />}{" "}
      Sync
    </>
  );

  return (
    <Dropdown className={classNames("nav-item fixed-height", className)} autoClose="outside" align="end">
      <Dropdown.Toggle className="nav-link" variant="link">
        {title}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Header className="text-decoration-underline pt-0 pb-1">Current Page</Dropdown.Header>
        <Dropdown.ItemText>
          Data Loaded: <SyncTime lastUpdated={pageOldestDataLoadedAt} />
        </Dropdown.ItemText>
        <Dropdown.Divider />
        <Dropdown.Header className="text-decoration-underline pt-0 pb-1">Database Sync</Dropdown.Header>
        <ManualSyncButton
          label="Sync BOMs"
          lastSynced={bomSyncMetadata?.lastUpdated ?? null}
          runSync={runBomSync}
          syncRunning={syncBOMRunning}
          setSyncRunning={setSyncBOMRunning}
        />
        <ManualSyncButton
          label="Sync Vendor-Item Catalog"
          lastSynced={vendorItemCatalogSyncMetadata?.lastUpdated ?? null}
          runSync={runVendorItemCatalogSync}
          syncRunning={syncVendorItemCatalogRunning}
          setSyncRunning={setSyncVendorItemCatalogRunning}
        />
        <ManualSyncButton
          label="Sync Others"
          lastSynced={otherSyncMetadata[0]?.lastUpdated}
          runSync={syncAllEntities}
          syncRunning={syncOthersRunning}
          setSyncRunning={setSyncOthersRunning}
        />
      </Dropdown.Menu>
    </Dropdown>
  );
};

const LoadingNavItem: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Dropdown className={"nav-item"}>
    <Dropdown.Toggle className="nav-link" variant="link" disabled={true}>
      {children}
    </Dropdown.Toggle>
  </Dropdown>
);
